import { defineComponent, ref, inject, onMounted, watch, nextTick } from 'vue';
import SInputText from '@/components/stemdo-components/s-input-text/SInputText.vue';
import SInputDate from '@/components/stemdo-components/s-input-date/SInputDate.vue';
import SAutocompleteMultiple from '@/sections/shared/explore/top-filter/filter/s-autocomplete-multiple/SAutocompleteMultiple.vue';
import SSelectorMultiple from '@/components/stemdo-components/s-selector-multiple/SSelectorMultiple.vue';
import TechnologyService from '@/sections/admin/entities/technology/technology.service';
import SInputTextarea from '@/components/stemdo-components/s-input-textarea/SInputTextarea.vue';
import { IExperience } from '@/shared/model/experience.model';
import { useI18N } from '@/plugins/i18n';
import useVuelidate from '@vuelidate/core';
import { experienceForm, experienceFormRules, fillExperienceForm, resetExperienceForm } from './experienceForm';
import ExpDeletedModal from '@/sections/shared/team-request-detail/buttons-group/modal/manager/expDeleted/ExpDeletedModal.vue';
import { FilterFields } from '@/sections/shared/explore/model/Filters';
import { IAtocompleteMultipleState } from '@/sections/shared/explore/top-filter/filter/s-autocomplete-multiple/sAutocompleteMultiple';

export default defineComponent({
  props: {
    experience: {
      type: Object as () => IExperience,
      default: undefined
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    onExperienceSave: {
      type: Function,
      required: true
    },
    onCloseModal: {
      type: Function,
      required: true
    },
    onExperienceDelete: {
      type: Function,
      required: true
    }
  },
  components: {
    SInputText,
    SInputDate,
    SAutocompleteMultiple,
    SSelectorMultiple,
    SInputTextarea,
    ExpDeletedModal
  },
  setup(props) {
    const i18n = useI18N();
    const technologyService: TechnologyService = inject('technologyService');
    const competenciesItems = ref([]);
    const vuelidate = useVuelidate(experienceFormRules, experienceForm);
    const isExpDeletedModalOpen = ref(false);
    const autocompleteRef = ref(null);

    onMounted(async () => {
      competenciesItems.value = (await technologyService.retrieve()).map(item => ({
        id: item.id,
        desc: item.name
      }));
    });

    watch(
      () => props.experience,
      value => {
        if (value) {
          fillExperienceForm(value, competenciesItems.value);
        }
      }
    );

    const competenciesField = FilterFields.Competency;
    const changeCompetenciesFilter = (field: FilterFields, newSelectedItems: IAtocompleteMultipleState[]) => {
      experienceForm.competencies = newSelectedItems.map(item => item.id);
    };

    const onSaveClick = async () => {
      const technologiesSeparatedByComma = experienceForm.competencies
        .map(competencyId => {
          const tech = competenciesItems.value.find(item => item.id === competencyId);
          return tech ? tech.desc : competencyId;
        })
        .join(';');

      const endDateValue = !experienceForm.endDate || experienceForm.endDate === '' ? null : new Date(experienceForm.endDate);

      const experience: IExperience = {
        id: props.experience?.id,
        title: experienceForm.position,
        company: experienceForm.company,
        sector: experienceForm.sector,
        initDate: experienceForm.startDate !== '' ? new Date(experienceForm.startDate) : null,
        endDate: endDateValue && endDateValue instanceof Date && !isNaN(endDateValue.getTime()) ? endDateValue : null,
        technologies: technologiesSeparatedByComma,
        description: experienceForm.description,
        validation: true
      };
      const result = await vuelidate.value.$validate();
      if (result === true) {
        resetExperienceForm();
        vuelidate.value.$reset();
        props.onExperienceSave(experience);
      }
    };

    const onDeleteClick = () => {
      isExpDeletedModalOpen.value = true;
    };

    const handleDeleteConfirm = () => {
      if (props.experience) {
        props.onExperienceDelete(props.experience);
      }
      resetExperienceForm();
      vuelidate.value.$reset();
      isExpDeletedModalOpen.value = false;
    };

    const handleDeleteCancel = () => {
      isExpDeletedModalOpen.value = false;
    };

    const closeModal = () => {
      resetExperienceForm();
      vuelidate.value.$reset();
      props.onCloseModal();
    };

    const onCreateTech = async (nombre: string) => {
      try {
        const nuevaTech = await technologyService.create({ name: nombre });
        if (!competenciesItems.value.some(item => String(item.id) === String(nuevaTech.id))) {
          competenciesItems.value.push({ id: String(nuevaTech.id), desc: nuevaTech.name });
        }
        experienceForm.competencies = [
          ...experienceForm.competencies.filter(id => String(id) !== String(nuevaTech.id)),
          String(nuevaTech.id)
        ];
        await nextTick();
        autocompleteRef.value?.clearSearchInput && autocompleteRef.value.clearSearchInput();
      } catch (error) {
        console.error('tech create:', error);
      }
    };

    return {
      vuelidate,
      experienceForm,
      competenciesItems,
      competenciesField,
      changeCompetenciesFilter,
      onSaveClick,
      onDeleteClick,
      closeModal,
      isExpDeletedModalOpen,
      handleDeleteConfirm,
      handleDeleteCancel,
      onCreateTech,
      autocompleteRef
    };
  }
});
